import { matchPath } from 'react-router-dom';

function isMiniQuizPath(pathname: string): boolean {
  return (
    matchPath(pathname, { path: '/mini-quiz', exact: true, strict: false }) !==
      null ||
    matchPath(pathname, { path: '/mini-quiz/:slug*', strict: false }) !== null
  );
}
import {
  ROUTE_MINI_QUIZ_AVOIDANCES,
  ROUTE_MINI_QUIZ_BUDGET,
  ROUTE_MINI_QUIZ_CUISINES,
  ROUTE_MINI_QUIZ_DISH_TYPES,
  ROUTE_MINI_QUIZ_EATING_HABITS,
  ROUTE_MINI_QUIZ_FOOD_FORMATS,
  ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO,
  ROUTE_MINI_QUIZ_HELLO,
  ROUTE_MINI_QUIZ_MACRO_NUTRIENT,
  ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
  ROUTE_MINI_QUIZ_PROTEIN,
  ROUTE_MINI_QUIZ_SWIPE,
} from '@hungryroot/routes';
import { AVOIDANCE_TAG_UNIVERSE } from '../defs/avoidances';
import {
  MINI_QUIZ_ROUTE_SEQUENCES,
  MINI_QUIZ_TAIL_ROUTES,
  type MiniQuizSequenceKey,
} from '../defs/mini-quiz-routes';
import { isMiniQuizTailSession } from '../defs/miniQuizTail';
import { ProteinTagTernaryRating, type Quiz } from '../defs/quiz';

export const MINI_QUIZ_QUESTION_ROUTES = [
  ROUTE_MINI_QUIZ_FOOD_FORMATS,
  ROUTE_MINI_QUIZ_EATING_HABITS,
  ROUTE_MINI_QUIZ_MACRO_NUTRIENT,
  ROUTE_MINI_QUIZ_DISH_TYPES,
  ROUTE_MINI_QUIZ_CUISINES,
  ROUTE_MINI_QUIZ_PROTEIN,
  ROUTE_MINI_QUIZ_AVOIDANCES,
  ROUTE_MINI_QUIZ_HELLO,
] as const;

/** Diet and household size are the only required mini-quiz question screens. */
export const MINI_QUIZ_REQUIRED_QUESTION_ROUTES = [
  ROUTE_MINI_QUIZ_EATING_HABITS,
  ROUTE_MINI_QUIZ_HELLO,
] as const;

export function isMiniQuizQuestionRoute(pathname: string): boolean {
  if (
    matchPath(pathname, {
      path: ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
      exact: true,
      strict: false,
    }) !== null
  ) {
    return true;
  }

  return MINI_QUIZ_QUESTION_ROUTES.some(
    (path) => matchPath(pathname, { path, exact: true, strict: false }) !== null
  );
}

export function isMiniQuizSwipeRoute(pathname: string): boolean {
  return (
    matchPath(pathname, {
      path: ROUTE_MINI_QUIZ_SWIPE,
      exact: true,
      strict: false,
    }) !== null
  );
}

export function isMiniQuizFoodPreferencesIntroRoute(pathname: string): boolean {
  return (
    matchPath(pathname, {
      path: ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO,
      exact: true,
      strict: false,
    }) !== null
  );
}

export function shouldShowMiniQuizHeader(pathname: string): boolean {
  return (
    isMiniQuizQuestionRoute(pathname) ||
    isMiniQuizSwipeRoute(pathname) ||
    isMiniQuizFoodPreferencesIntroRoute(pathname)
  );
}

export function isMiniQuizTailRoute(pathname: string): boolean {
  return MINI_QUIZ_TAIL_ROUTES.some(
    (path) => matchPath(pathname, { path, exact: true, strict: false }) !== null
  );
}

export function isMiniQuizMainSequencePath(pathname: string): boolean {
  return isMiniQuizPath(pathname) && !isMiniQuizTailRoute(pathname);
}

export function isActiveMiniQuizTailRoute(pathname: string): boolean {
  return isMiniQuizTailRoute(pathname) && isMiniQuizTailSession();
}

export function isMiniQuizOptionalQuestionRoute(pathname: string): boolean {
  if (!isMiniQuizQuestionRoute(pathname)) {
    return false;
  }

  return !MINI_QUIZ_REQUIRED_QUESTION_ROUTES.some(
    (path) => matchPath(pathname, { path, exact: true, strict: false }) !== null
  );
}

function isMiniQuizSequenceKey(
  sequenceKey: string | undefined
): sequenceKey is MiniQuizSequenceKey {
  return (
    sequenceKey === 'miniQuizVariant1' || sequenceKey === 'miniQuizVariant2'
  );
}

export function getMiniQuizStepLabel({
  pathname,
  sequenceKey,
}: {
  pathname: string;
  sequenceKey: string | undefined;
}): { current: number; total: number } | null {
  if (!isMiniQuizSequenceKey(sequenceKey)) {
    return null;
  }

  const run: string[] = [];
  for (const entry of MINI_QUIZ_ROUTE_SEQUENCES[sequenceKey]) {
    if (!isMiniQuizQuestionRoute(entry.path)) {
      break;
    }
    if (entry.path === ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM) {
      continue;
    }
    run.push(entry.path);
  }

  const pathnameForStep = matchPath(pathname, {
    path: ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
    exact: true,
    strict: false,
  })
    ? ROUTE_MINI_QUIZ_MACRO_NUTRIENT
    : pathname;

  const index = run.findIndex(
    (path) =>
      matchPath(pathnameForStep, { path, exact: true, strict: false }) !== null
  );
  if (index === -1) {
    return null;
  }

  return { current: index + 1, total: run.length };
}

function matchesRoute(pathname: string, path: string): boolean {
  return matchPath(pathname, { path, exact: true, strict: false }) !== null;
}

function hasNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function hasHouseholdPeople(quiz: Quiz): boolean {
  return (
    (quiz.adultsCount ?? 0) +
      (quiz.childrenCount ?? 0) +
      (quiz.youngChildrenCount ?? 0) >
    0
  );
}

function hasAbsolutelyRating(
  ratings: Quiz['meats'] | Quiz['seafood'] | Quiz['plants']
): boolean {
  return (ratings ?? []).some(
    (rating) => rating.rated === ProteinTagTernaryRating.Absolutely
  );
}

export function getMiniQuizAnswerState({
  pathname,
  quiz,
}: {
  pathname: string;
  quiz: Quiz;
}): {
  hasValue: boolean;
} {
  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_EATING_HABITS)) {
    return {
      hasValue: quiz.habits !== null,
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_FOOD_FORMATS)) {
    return {
      hasValue: hasNonEmptyArray(quiz.foodFormats),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_DISH_TYPES)) {
    return {
      hasValue: hasNonEmptyArray(quiz.lunch) || hasNonEmptyArray(quiz.dinner),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_MACRO_NUTRIENT)) {
    return {
      hasValue: hasNonEmptyArray(quiz.macroNutrient),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM)) {
    return {
      hasValue: true,
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_CUISINES)) {
    return {
      hasValue: (quiz.cuisines ?? []).some((item) => item.liked === 1),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_PROTEIN)) {
    return {
      hasValue:
        hasAbsolutelyRating(quiz.meats) ||
        hasAbsolutelyRating(quiz.seafood) ||
        hasAbsolutelyRating(quiz.plants),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_AVOIDANCES)) {
    const avoidanceTagIds = new Set(AVOIDANCE_TAG_UNIVERSE);
    const hasAvoidanceTag = (quiz.dislike ?? []).some((id) =>
      avoidanceTagIds.has(id)
    );
    return {
      hasValue: hasNonEmptyArray(quiz.dietary) || hasAvoidanceTag,
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_HELLO)) {
    return {
      hasValue: hasHouseholdPeople(quiz),
    };
  }

  if (matchesRoute(pathname, ROUTE_MINI_QUIZ_BUDGET)) {
    return {
      hasValue: quiz.budget != null,
    };
  }

  return { hasValue: false };
}
